import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useToggle from 'shared/hooks/useToggle';

import { GetPurchaseInvoiceRequestParams } from 'features/purchase-invoices/types';
import { purchaseInvoicesApi } from 'features/purchase-invoices/api';

export default function usePurchaseInvoiceData(invoiceId: string | null) {
  const coopId = useCooperativeId();

  const queryParams = useMemo(() => {
    if (coopId && invoiceId) {
      return {
        coopId,
        invoiceId,
      } as GetPurchaseInvoiceRequestParams;
    }

    return skipToken;
  }, [coopId, invoiceId]);

  const [isShowedDetails, toggleIsShowedDetails] = useToggle(false);

  const purchaseInvoiceQueryState =
    purchaseInvoicesApi.useGetPurchaseInvoiceQuery(queryParams, {
      refetchOnMountOrArgChange: true,
    });
  const purchaseInvoiceMonthsQueryState =
    purchaseInvoicesApi.useGetPurchaseInvoiceMonthsQuery(queryParams, {
      refetchOnMountOrArgChange: true,
    });
  const ledgerAccountEventsQueryState =
    purchaseInvoicesApi.useGetLedgerAccountEventsQuery(queryParams, {
      refetchOnMountOrArgChange: true,
    });

  const ledgerAccountEvents = useMemo(() => {
    return ledgerAccountEventsQueryState.data ?? [];
  }, [ledgerAccountEventsQueryState.data]);

  const isLoadingPurchaseInvoice =
    purchaseInvoiceQueryState.isLoading || purchaseInvoiceQueryState.isFetching;
  const isLoadingPurchaseInvoiceMonths =
    purchaseInvoiceMonthsQueryState.isLoading ||
    purchaseInvoiceMonthsQueryState.isFetching;
  const isLoadingLedgerAccountEvents =
    ledgerAccountEventsQueryState.isLoading ||
    ledgerAccountEventsQueryState.isFetching;
  const isError = purchaseInvoiceQueryState.isError;

  return {
    isError,
    isLoadingLedgerAccountEvents,
    isLoadingPurchaseInvoice,
    isLoadingPurchaseInvoiceMonths,
    isShowedDetails,
    ledgerAccountEvents,
    purchaseInvoiceMonthsQueryState,
    purchaseInvoiceQueryState,
    toggleIsShowedDetails,
  };
}
